import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSidebar } from '../contexts/SidebarContext';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { apiService } from '../services/api';

interface DashboardSidebarProps {
  isCollapsed?: boolean;
}

const DashboardSidebar: React.FC<DashboardSidebarProps> = ({ isCollapsed = false }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { darkMode } = useTheme();
  const [searchQuery, setSearchQuery] = useState<string>(searchParams.get('search') || '');
  const [filterType, setFilterType] = useState<'my_task' | 'my_team'>(
    (searchParams.get('filter') as 'my_task' | 'my_team') || 'my_task'
  );
  const [myTaskCount, setMyTaskCount] = useState(0);
  const [myTeamCount, setMyTeamCount] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadCounts();
  }, [user]);

  const loadCounts = async () => {
    if (!user?.id) return;
    
    setLoading(true);
    try {
      // Load My Task count (issued_by_me + assigned_to_me)
      const myTasks = await apiService.getTasks({
        is_active: 1,
        filter_type: 'issued_by_me'
      });
      const assignedTasks = await apiService.getTasks({
        is_active: 1,
        filter_type: 'assigned_to_me'
      });
      const myTaskIds = new Set([
        ...myTasks.map((t: any) => t.id),
        ...assignedTasks.map((t: any) => t.id)
      ]);
      setMyTaskCount(myTaskIds.size);

      // Load My Team count
      const teamTasks = await apiService.getTasks({
        is_active: 1,
        filter_type: 'my_team'
      });
      setMyTeamCount(teamTasks.length);
    } catch (err: any) {
      console.error('Error loading counts:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (filter: 'my_task' | 'my_team') => {
    setFilterType(filter);
    const params = new URLSearchParams(searchParams);
    params.set('filter', filter);
    setSearchParams(params);
  };

  if (isCollapsed) {
    return (
      <>
        <div className="px-2 py-4 space-y-2">
          <button 
            className={`w-full flex items-center justify-center p-2 rounded-lg transition-colors ${
              searchQuery 
                ? 'text-violet-600 bg-violet-50' 
                : darkMode
                  ? 'text-gray-300 hover:bg-gray-700'
                  : 'text-gray-600 hover:bg-gray-100'
            }`}
            title={searchQuery ? `Search: ${searchQuery}` : 'Search'}
          >
            <i className="fa-solid fa-magnifying-glass text-sm"></i>
          </button>
        </div>
        <div className={`px-2 py-4 border-t space-y-1 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <button 
            onClick={() => handleFilterChange('my_task')}
            className={`w-full flex items-center justify-center p-2 rounded-lg transition-colors ${
              filterType === 'my_task' 
                ? 'text-violet-600 bg-violet-50' 
                : darkMode
                  ? 'text-gray-300 hover:bg-gray-700'
                  : 'text-gray-600 hover:bg-gray-100'
            }`}
            title="My Tasks"
          >
            <i className="fa-solid fa-user text-sm"></i>
          </button>
          <button 
            onClick={() => handleFilterChange('my_team')}
            className={`w-full flex items-center justify-center p-2 rounded-lg transition-colors ${
              filterType === 'my_team' 
                ? 'text-violet-600 bg-violet-50' 
                : darkMode
                  ? 'text-gray-300 hover:bg-gray-700'
                  : 'text-gray-600 hover:bg-gray-100'
            }`}
            title="My Team"
          >
            <i className="fa-solid fa-users text-sm"></i>
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="px-4 pt-6 pb-4">
        <div className="relative">
          <i className={`fa-solid fa-magnifying-glass absolute left-3 top-3 text-sm ${
            darkMode ? 'text-gray-500' : 'text-gray-400'
          }`}></i>
          <input 
            type="text" 
            placeholder="Search agreements..." 
            value={searchQuery}
            onChange={(e) => {
              const value = e.target.value;
              setSearchQuery(value);
              
              // Update URL search params
              const params = new URLSearchParams(searchParams);
              if (value.trim()) {
                params.set('search', value.trim());
              } else {
                params.delete('search');
              }
              setSearchParams(params);
            }}
            className={`w-full pl-9 pr-4 py-2 text-sm border rounded-lg focus:outline-none focus:border-violet-500 transition-colors ${
              darkMode
                ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500'
                : 'border-gray-300'
            }`}
          />
          {searchQuery && (
            <button
              onClick={() => {
                setSearchQuery('');
                const params = new URLSearchParams(searchParams);
                params.delete('search');
                setSearchParams(params);
              }}
              className={`absolute right-3 top-3 transition-colors ${
                darkMode ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              <i className="fa-solid fa-times text-sm"></i>
            </button>
          )}
        </div>
      </div>
      <div className={`px-4 py-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        <span className={`text-xs font-semibold uppercase tracking-wider ${
          darkMode ? 'text-gray-400' : 'text-gray-500'
        }`}>Views</span>
        <ul className="mt-2 space-y-1">
          <li>
            <button 
              onClick={() => handleFilterChange('my_task')}
              className={`w-full flex items-center px-3 py-2 text-sm rounded-lg transition-colors ${
                filterType === 'my_task' 
                  ? 'bg-violet-50 text-violet-600' 
                  : darkMode
                    ? 'text-gray-300 hover:bg-gray-700'
                    : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <i className={`fa-solid fa-user mr-3 text-sm ${
                filterType === 'my_task' 
                  ? 'text-violet-600' 
                  : darkMode ? 'text-gray-400' : 'text-gray-500'
              }`}></i>
              <span className={filterType === 'my_task' ? 'font-medium' : ''}>My Tasks</span>
              {loading ? (
                <span className={`ml-auto text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>...</span>
              ) : (
                <span className={`ml-auto text-xs ${
                  filterType === 'my_task' 
                    ? 'text-violet-600' 
                    : darkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  {myTaskCount}
                </span>
              )}
            </button>
          </li>
          <li>
            <button 
              onClick={() => handleFilterChange('my_team')}
              className={`w-full flex items-center px-3 py-2 text-sm rounded-lg transition-colors ${
                filterType === 'my_team' 
                  ? 'bg-violet-50 text-violet-600' 
                  : darkMode
                    ? 'text-gray-300 hover:bg-gray-700'
                    : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <i className={`fa-solid fa-users mr-3 text-sm ${
                filterType === 'my_team' 
                  ? 'text-violet-600' 
                  : darkMode ? 'text-gray-400' : 'text-gray-500'
              }`}></i>
              <span className={filterType === 'my_team' ? 'font-medium' : ''}>My Team</span>
              {loading ? (
                <span className={`ml-auto text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>...</span>
              ) : (
                <span className={`ml-auto text-xs ${
                  filterType === 'my_team' 
                    ? 'text-violet-600' 
                    : darkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  {myTeamCount}
                </span>
              )}
            </button>
          </li>
        </ul>
      </div>
    </>
  );
};

export default DashboardSidebar;

